import { createPatch, Patch } from 'rfc6902';
import deepClone from '../utilities/deepClone';

const emptyPatch: Patch = [];

function optimizedDiff(prev: unknown, next: unknown): Patch | undefined {
  if (Object.is(prev, next)) {
    // The default implementation doesn't seem to do this check at all.
    // It is guaranteed that there are no difference when comparing the same instance to itself.
    return emptyPatch;
  }
  // Fall back to the default diffing
  return undefined;
}

/**
 * Calculates the difference between the two values, expected to be complex object structures, and returns a
 * list of JSONPatch operations that when applied to the first value, the result ends up to equal to the second value.
 * If an empty array is returned, it means that the values are equal.
 *
 * When working with mutable structures, e.g. with DolanProxy, then you should pass `true` as `cloneValues` parameter,
 * which will perform safe deep-cloning for any values ending up to the patch. This way any mutations later
 * affecting any of the input structures won't accidentally mutate the returned patch.
 *
 * For performance reasons, deep-cloning is disabled by default.
 *
 * @param previous the start state of the value
 * @param next the end state of the value
 * @param cloneValues whether or not to deep-clone values ending up to the patch
 * @returns list of JSONPatch operations
 */
export default function getDiffPatch(previous: unknown, next: unknown, cloneValues: boolean = false): Patch {
  const patch = createPatch(previous, next, optimizedDiff);
  if (!cloneValues) return patch;
  return patch.map((operation) => {
    if ('value' in operation) {
      // NOTE: If the value is a DolanProxy, ComponentInstanceProxy etc. it's toJSON() will be called by JSON.stringify
      return {
        ...operation,
        value: deepClone(operation.value),
      };
    }
    return operation;
  });
}
